import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const {
  FiCheckCircle,
  FiClock,
  FiFileText,
  FiMessageSquare,
  FiPlay,
  FiFlag,
  FiAlertCircle,
  FiChevronDown,
  FiChevronRight,
  FiFolder,
  FiTarget
} = FiIcons;

/**
 * Timeline Event Component
 * Displays individual timeline event with icon, title, description, and metadata
 */
const TimelineEvent = ({ event, isLast, isNested }) => {
  const getEventIcon = (type) => {
    switch (type) {
      case 'case_created':
        return FiFolder;
      case 'action_completed':
        return FiCheckCircle;
      case 'stage_started':
        return FiPlay;
      case 'stage_completed':
        return FiFlag;
      case 'document_uploaded':
        return FiFileText;
      case 'note_added':
        return FiMessageSquare;
      default:
        return FiClock;
    }
  };

  const getEventColor = (type) => {
    switch (type) {
      case 'case_created':
        return 'bg-purple-100 text-purple-600 border-purple-300';
      case 'action_completed':
        return 'bg-green-100 text-green-600 border-green-300';
      case 'stage_started':
        return 'bg-blue-100 text-blue-600 border-blue-300';
      case 'stage_completed':
        return 'bg-indigo-100 text-indigo-600 border-indigo-300';
      case 'document_uploaded':
        return 'bg-gray-100 text-gray-600 border-gray-300';
      case 'note_added':
        return 'bg-yellow-100 text-yellow-600 border-yellow-300';
      default:
        return 'bg-gray-100 text-gray-600 border-gray-300';
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    if (diffDays < 30) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
    return formatDate(dateString);
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      className={`relative flex items-start space-x-3 ${isNested ? 'ml-8' : ''} pb-6`}
    >
      {/* Timeline Line */}
      {!isLast && (
        <div className={`absolute ${isNested ? 'left-5' : 'left-5'} top-10 bottom-0 w-0.5 bg-gray-200`} />
      )}

      {/* Event Icon */}
      <div className={`relative z-10 flex-shrink-0 w-10 h-10 rounded-full border-2 flex items-center justify-center ${getEventColor(event.event_type)}`}>
        <SafeIcon icon={getEventIcon(event.event_type)} className="w-4 h-4" />
      </div>

      {/* Event Content */}
      <div className="flex-1 min-w-0">
        <div className="bg-white border border-gray-200 rounded-lg p-3 shadow-sm hover:shadow-md transition-shadow">
          {/* Header */}
          <div className="flex items-start justify-between mb-2">
            <div className="flex-1">
              <h5 className="font-semibold text-gray-900 text-sm mb-1">{event.event_title}</h5>
              {event.stage_name && (
                <p className="text-xs text-gray-500 mb-1">
                  Stage: <span className="font-medium">{event.stage_name}</span>
                </p>
              )}
            </div>
            
            {/* Badges */}
            <div className="flex items-center space-x-1 ml-2">
              {event.is_legal && (
                <span className="text-xs font-bold text-red-600 bg-red-50 px-2 py-0.5 rounded">
                  ⚖️
                </span>
              )}
              {event.is_acas && (
                <span className="text-xs font-bold text-green-600 bg-green-50 px-2 py-0.5 rounded">
                  🟢
                </span>
              )}
            </div>
          </div>

          {/* Description */}
          {event.event_description && (
            <p className="text-xs text-gray-600 mb-2 whitespace-pre-line">
              {event.event_description}
            </p>
          )}

          {/* Timestamp */}
          <div className="flex items-center space-x-2 text-xs text-gray-500">
            <SafeIcon icon={FiClock} className="w-3 h-3" />
            <span>{getTimeAgo(event.event_date)}</span>
            <span className="text-gray-400">•</span>
            <span>{formatDate(event.event_date)}</span>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

/**
 * Case Group Component
 * Groups events by case with expand/collapse functionality
 */
const CaseGroup = ({ caseId, caseData, events, isLast }) => {
  const [isExpanded, setIsExpanded] = useState(true);

  const getCaseStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-blue-100 text-blue-800';
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'on-hold':
        return 'bg-yellow-100 text-yellow-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'text-red-600';
      case 'medium':
        return 'text-yellow-600';
      case 'low':
        return 'text-green-600';
      default:
        return 'text-gray-600';
    }
  };

  return (
    <div className="mb-6">
      {/* Case Header */}
      <div 
        className="bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-lg p-4 cursor-pointer hover:shadow-md transition-shadow"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3 flex-1">
            <SafeIcon 
              icon={isExpanded ? FiChevronDown : FiChevronRight} 
              className="text-blue-600 w-5 h-5"
            />
            <div className="flex-1">
              <div className="flex items-center space-x-2 mb-1">
                <h3 className="font-bold text-gray-900">{caseData.case_title}</h3>
                <span className="text-xs text-gray-500">({caseData.case_number})</span>
              </div>
              <div className="flex items-center space-x-2 text-sm">
                <span className="text-gray-600">{caseData.case_type}</span>
                <span className="text-gray-400">•</span>
                <span className={`px-2 py-0.5 rounded text-xs font-medium ${getCaseStatusColor(caseData.case_status)}`}>
                  {caseData.case_status?.toUpperCase()}
                </span>
                {caseData.case_priority && (
                  <>
                    <span className="text-gray-400">•</span>
                    <span className={`text-xs font-medium ${getPriorityColor(caseData.case_priority)}`}>
                      {caseData.case_priority?.toUpperCase()} PRIORITY
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <div className="text-right">
              <p className="text-xs text-gray-500">Events</p>
              <p className="text-lg font-bold text-blue-600">{events.length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Case Events */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mt-4 ml-4"
          >
            {events.map((event, index) => (
              <TimelineEvent
                key={`${event.case_id}-${event.event_type}-${event.event_date}-${index}`}
                event={event}
                isLast={isLast && index === events.length - 1}
                isNested={true}
              />
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

/**
 * Staff Timeline Component
 * Displays chronological timeline of all staff-related events grouped by case
 */
const StaffTimeline = ({ events, loading }) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!events || events.length === 0) {
    return (
      <div className="text-center py-12">
        <SafeIcon icon={FiClock} className="text-4xl text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">No Timeline Events</h3>
        <p className="text-gray-600">
          Timeline events will appear here as this staff member is involved in cases.
        </p>
      </div>
    );
  }

  // Group events by case
  const eventsByCase = events.reduce((groups, event) => {
    const caseId = event.case_id;
    if (!groups[caseId]) {
      groups[caseId] = {
        caseData: {
          case_id: event.case_id,
          case_number: event.case_number,
          case_title: event.case_title,
          case_type: event.case_type,
          case_status: event.case_status,
          case_priority: event.case_priority
        },
        events: []
      };
    }
    groups[caseId].events.push(event);
    return groups;
  }, {});

  // Sort cases by most recent event
  const sortedCases = Object.entries(eventsByCase).sort((a, b) => {
    const aLatest = new Date(a[1].events[0].event_date);
    const bLatest = new Date(b[1].events[0].event_date);
    return bLatest - aLatest;
  });

  // Calculate summary stats
  const stats = {
    totalCases: sortedCases.length,
    totalEvents: events.length,
    actionsCompleted: events.filter(e => e.event_type === 'action_completed').length,
    documentsUploaded: events.filter(e => e.event_type === 'document_uploaded').length,
    legalActions: events.filter(e => e.is_legal).length,
    acasActions: events.filter(e => e.is_acas).length
  };

  return (
    <div className="space-y-6">
      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
          <div className="flex items-center space-x-2 mb-1">
            <SafeIcon icon={FiFolder} className="text-purple-600 w-4 h-4" />
            <span className="text-xs font-medium text-purple-900">Cases</span>
          </div>
          <p className="text-xl font-bold text-purple-600">{stats.totalCases}</p>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <div className="flex items-center space-x-2 mb-1">
            <SafeIcon icon={FiTarget} className="text-blue-600 w-4 h-4" />
            <span className="text-xs font-medium text-blue-900">Events</span>
          </div>
          <p className="text-xl font-bold text-blue-600">{stats.totalEvents}</p>
        </div>

        <div className="bg-green-50 border border-green-200 rounded-lg p-3">
          <div className="flex items-center space-x-2 mb-1">
            <SafeIcon icon={FiCheckCircle} className="text-green-600 w-4 h-4" />
            <span className="text-xs font-medium text-green-900">Actions</span>
          </div>
          <p className="text-xl font-bold text-green-600">{stats.actionsCompleted}</p>
        </div>

        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
          <div className="flex items-center space-x-2 mb-1">
            <SafeIcon icon={FiFileText} className="text-gray-600 w-4 h-4" />
            <span className="text-xs font-medium text-gray-900">Docs</span>
          </div>
          <p className="text-xl font-bold text-gray-600">{stats.documentsUploaded}</p>
        </div>

        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <div className="flex items-center space-x-2 mb-1">
            <span className="text-xs font-medium text-red-900">⚖️ Legal</span>
          </div>
          <p className="text-xl font-bold text-red-600">{stats.legalActions}</p>
        </div>

        <div className="bg-green-50 border border-green-200 rounded-lg p-3">
          <div className="flex items-center space-x-2 mb-1">
            <span className="text-xs font-medium text-green-900">🟢 ACAS</span>
          </div>
          <p className="text-xl font-bold text-green-600">{stats.acasActions}</p>
        </div>
      </div>

      {/* Timeline - Grouped by Case */}
      <div className="space-y-4">
        <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
          Timeline by Case
        </h3>
        
        {sortedCases.map(([caseId, { caseData, events: caseEvents }], index) => (
          <CaseGroup
            key={caseId}
            caseId={caseId}
            caseData={caseData}
            events={caseEvents}
            isLast={index === sortedCases.length - 1}
          />
        ))}
      </div>
    </div>
  );
};

export default StaffTimeline;
